import createCachedSelector from 're-reselect';

import { AttachmentType } from '../../state/attachments/types';
import type {
  AttachmentReference,
  File,
  FileWithType,
  ImageFile,
  ImageFileReference,
  ImageFileWithType,
  MediaLightboxInstance,
  Message,
  PreviewableAttachmentWithType,
  SharePointFileLink,
  SharePointFileLinkWithType,
  WebImage,
  WebImageReference,
  WebImageWithType,
  YammerState,
} from '../../state/types';
import { getFulfilledClientSharePointWebLinkEmbeds } from '../clientSharePointWebLinkEmbeds/selectors';
import { getFulfilledFiles } from '../files/selectors';
import { getFulfilledImageFiles } from '../imageFiles/selectors';
import { getFulfilledSharePointFileLinks } from '../sharePointFileLinks/selectors';
import { getFulfilledWebImages } from '../webImages/selectors';

const attachmentReferenceIsImageFile = (attachment: AttachmentReference): attachment is ImageFileReference =>
  attachment.schema === 'imageFiles';

const attachmentReferenceIsWebImage = (attachment: AttachmentReference): attachment is WebImageReference =>
  attachment.schema === 'webImages';

type GetMediaLightboxInstance = (state: YammerState) => MediaLightboxInstance | undefined;
export const getMediaLightboxInstance: GetMediaLightboxInstance = (state) => state.mediaLightbox.instance;

const imageFileWithType = (item: ImageFile): ImageFileWithType => ({
  item,
  type: AttachmentType.ImageFile,
});

const webImageWithType = (item: WebImage): WebImageWithType => ({
  item,
  type: AttachmentType.WebImage,
});

const fileWithType = (item: File): FileWithType => ({
  item,
  type: AttachmentType.File,
});

const sharePointFileLinkWithType = (item: SharePointFileLink): SharePointFileLinkWithType => ({
  item,
  type: AttachmentType.SharePointFileLink,
});

type GetMediaLightboxCurrentItem = (state: YammerState) => PreviewableAttachmentWithType | undefined;
/**
 * @deprecated PERFORMANCE This selector will return a new object instance any time any sub-selector changes.
 * This should be deleted and replaced by a `getMediaLightboxCurrentItemId` selector, then that id can be
 * passed to field selectors so components can select only the specific (primitive) fields they require and
 * avoid unnecessary re-renders.
 */
export const getMediaLightboxCurrentItem: GetMediaLightboxCurrentItem = createCachedSelector(
  getMediaLightboxInstance,
  getFulfilledImageFiles,
  getFulfilledWebImages,
  getFulfilledFiles,
  getFulfilledSharePointFileLinks,
  getFulfilledClientSharePointWebLinkEmbeds,
  (lightbox, imageFiles, webImages, files, sharePointFileLinks, clientSharePointWebLinkEmbeds) => {
    if (!lightbox || !lightbox.currentItem) {
      return undefined;
    }

    const { id } = lightbox.currentItem;

    if (attachmentReferenceIsImageFile(lightbox.currentItem)) {
      return imageFileWithType(imageFiles[id]);
    }

    if (attachmentReferenceIsWebImage(lightbox.currentItem)) {
      return webImageWithType(webImages[id]);
    }

    const hasSharePointEmbedPreviewUrl = clientSharePointWebLinkEmbeds[id]?.embedUrl;

    if (hasSharePointEmbedPreviewUrl) {
      if (sharePointFileLinks[id]?.id) {
        return sharePointFileLinkWithType({
          ...sharePointFileLinks[id],
          fullPageEditorUrl: sharePointFileLinks[id].url,
          embeddablePreviewUrl: clientSharePointWebLinkEmbeds[id].embedUrl,
        });
      }

      return;
    }

    return fileWithType(files[id]);
  }
)(() => 'cache');

type GetMediaLightboxMessageId = (state: YammerState) => Message['id'] | undefined;
export const getMediaLightboxMessageId: GetMediaLightboxMessageId = (state) => {
  const instance = getMediaLightboxInstance(state);

  return instance && instance.messageId;
};
